/**
 * Der Klang des Tages — beide Fassungen nebeneinander. (08.09.2026)
 *
 * ## Woher das kommt
 *
 * Martin: *„Kann man da beide Buttons anlegen, sodass ich dann beide hören
 * kann? Das wäre ganz großartig, und dann gleich zur Musik auch einen
 * Download-Button. In der Mediathek sollte es ja auch liegen."*
 *
 * Der Hintergrund: `ESSENCE_MUSIC_PROVIDER=beide` erzeugt seit dem 06.09.
 * jeden Abend ZWEI Stücke aus demselben Kompositionsplan — eines von
 * ElevenLabs, eines von Lyria. Nur sehen konnte er immer nur eines. Die
 * zweite Fassung lag in der Mediathek und war vom Spiegel aus unerreichbar,
 * weil jede Abfrage genau eine Datei zurückgab.
 *
 * ## Der Bau
 *
 * Ein Kasten je Anbieter: Name, Spieler (wenn es die Fassung gibt), und die
 * beiden Knöpfe, die genau diese Fassung erzeugen oder ersetzen. Darunter
 * einmal der Liedtext — beide Stücke folgen demselben Plan, zwei Textblöcke
 * wären dieselbe Strophe zweimal.
 *
 * ⚠️ Diese Datei ersetzt ZWEI fast gleiche Blöcke in app.ts (heutiger
 *    Spiegel und Verlaufstag, je rund 120 Zeilen). Sie unterschieden sich
 *    in Kleinigkeiten, die keine Absicht waren: der Verlaufstag hatte kein
 *    Herz, und sein „Neu erzeugen" schickte `force` nur, wenn schon Musik
 *    da war — genau die Zeile, die Martin heute früh die Fehlmeldung
 *    beschert hat. Ein Ort, eine Wahrheit.
 */
import { relativeZeit } from "./relative-time.js";
import { spiegelStandardTag } from "../../shared/spiegel-tag.js";
import { istHauptnutzer } from "./hauptnutzer.js";

/** Eine Fassung des Tages. */
export interface KlangStueck {
  url: string;
  anbieter: "elevenlabs" | "lyria";
  /** 1 = die erste Fassung des Tages, 2 = die naechste, … */
  nr?: number;
  /**
   * Was in DIESER Fassung gesungen wird. Fehlt bei Stücken von vor dem
   * 09.09.2026 — dann gilt der Tages-Text als beste verfügbare Auskunft.
   */
  lyrics?: string;
  /** Ohne Gesang — so bestellt oder vom Sicherheitsfilter erzwungen. */
  instrumental?: boolean;
  /**
   * Ticket 2228 (13.09.2026): wann DIESES Stueck komponiert wurde. Der
   * Abendlauf schreibt den Tages-Text neu, laesst ein bestehendes
   * Tages-Stueck aber stehen — Absicht (Kosten/Laufzeit), soll aber
   * sichtbar sein statt wie ein Fehler zu wirken. Siehe `komponiertHinweis`.
   */
  createdAt?: string;
}

/**
 * "Komponiert vor 14h." — nur wenn das der Rede wert ist.
 *
 * ⚠️ Unter drei Stunden bleibt der Kasten still: frisch erzeugte Musik
 *    braucht keinen Zeitstempel, das waere Rauschen. Erst eine spuerbare
 *    Pause verdient den Hinweis — dieselbe Grenze, mit der server/
 *    zeitgefuehl.ts (Ticket 2231) "vorhin" von "gerade eben" trennt.
 */
export function komponiertHinweis(
  createdAt: string | undefined,
  jetzt = Date.now(),
): string {
  if (!createdAt) return "";
  const dann = new Date(createdAt).getTime();
  if (!Number.isFinite(dann)) return "";
  if (jetzt - dann < 3 * 60 * 60_000) return "";
  return `Komponiert ${relativeZeit(createdAt, jetzt)}.`;
}

/**
 * Tragen alle Fassungen denselben Wortlaut? Dann genügt EIN Block.
 *
 * ⚠️ Bis 09.09.2026 stand hier die Annahme, das sei immer so („beide Stücke
 *    folgen demselben Plan"). Sie hielt nur innerhalb EINES Laufs: Ein
 *    zweiter Druck komponiert einen neuen Plan, die alten Stücke bleiben
 *    liegen. Und Lyria singt ohnehin seinen eigenen Wortlaut.
 */
/**
 * Derselbe Wortlaut, andere Notation — auf den SINGBAREN Kern bringen.
 *
 * ⚠️⚠️ 15.09.2026 — Martin: *„Warum werden zweimal die Liedertexte angezeigt,
 *    wenn es doch immer der gleiche Text ist?"*
 *
 * Er hatte recht, und der Vergleich unten hatte trotzdem recht: An den echten
 * Stuecken vom 14.09. nachgesehen, stand dort
 *
 *     ElevenLabs:  Ein Raum voller Echos, so weit,
 *     Lyria:       [[A0]]
 *                  [:] Ein Raum voller Echos, so weit,
 *
 * Lyria bekommt DENSELBEN Text mit seinen eigenen Struktur-Markern — dazu ein
 * anderes Hochkomma. Zeichenweise verglichen sind das zwei Texte, gesungen
 * ist es einer.
 *
 * Diese Funktion entfernt nur NOTATION, keine Worte: Abschnittsmarken wie
 * `[[A0]]`, Zeilenpraefixe wie `[:]`, Regieanweisungen in geschweiften
 * Klammern, und sie vereinheitlicht Hochkomma und Leerraum.
 */
export function singbarerKern(text: string): string {
  return (text ?? "")
    .replace(/\[\[[^\]]*\]\]/g, " ") // [[A0]], [[B1]] — Abschnittsmarken
    .replace(/^\s*\[:\]\s?/gm, "") // [:] am Zeilenanfang
    .replace(/\{[^}]*\}/g, " ") // {solo electric guitar …}
    .replace(/\[[^\]\n]{0,40}\]/g, " ") // [Verse 1], [Chorus]
    .replace(/[\u2018\u2019\u02BC\u00B4`]/g, "'") // Hochkomma vereinheitlichen
    .replace(/[ \t]+/g, " ")
    .replace(/\s*\n\s*/g, "\n")
    .replace(/\n{2,}/g, "\n")
    .trim();
}

/**
 * Tragen alle Fassungen denselben Wortlaut? Dann genügt EIN Block.
 *
 * ⚠️ Bis 09.09.2026 stand hier die Annahme, das sei immer so („beide Stücke
 *    folgen demselben Plan"). Sie hielt nur innerhalb EINES Laufs: Ein
 *    zweiter Druck komponiert einen neuen Plan, die alten Stücke bleiben
 *    liegen.
 *
 * ⚠️ 15.09.2026: Verglichen wird der SINGBARE Kern, nicht die Notation —
 *    sonst zaehlen Lyrias Struktur-Marken als anderer Text. Angezeigt wird
 *    die lesbarste Fassung: die ohne Marken.
 */
export function gemeinsamerText(
  stuecke: readonly KlangStueck[],
  tagesText: string,
): string | null {
  if (stuecke.length === 0) return tagesText.trim() || null;
  const roh = stuecke.map((s) => (s.lyrics ?? tagesText).trim());
  const kerne = new Set(roh.map(singbarerKern));
  if (kerne.size !== 1) return null;
  const [kern] = [...kerne];
  if (!kern) return null;
  /*
   * Die Fassung ohne Notation ist die, die sich am wenigsten vom eigenen
   * Kern unterscheidet — so bleibt der Zeilenfall des Originals erhalten,
   * statt den bereinigten Text anzuzeigen.
   */
  let beste = roh[0] ?? kern;
  let abstand = Infinity;
  for (const t of roh) {
    const d = t.length - singbarerKern(t).length;
    if (d < abstand) {
      abstand = d;
      beste = t;
    }
  }
  return beste || null;
}

/**
 * Die Anbieter in der Reihenfolge, in der sie stehen sollen.
 *
 * ⚠️ ElevenLabs zuerst, weil es die eingestellte Hauptfassung ist. Wer die
 *    Reihenfolge nach „was gerade da ist" sortiert, lässt die Kästen bei
 *    jedem Öffnen springen.
 */
const ANBIETER: ReadonlyArray<{ id: KlangStueck["anbieter"]; name: string }> = [
  { id: "elevenlabs", name: "ElevenLabs" },
  { id: "lyria", name: "Lyria" },
];

/**
 * Welcher Anbieter allein steht, wenn nicht verglichen wird. (16.09.2026)
 *
 * Martin: *„Bitte Klang des Tages nur von Eleven Labs erzeugen lassen oder
 * Lyria (was guenstiger ist), und die anderen ein A/B bitte ausblenden, damit
 * sie nicht unnoetig verwirrt werden. Einfachheit geht vor. Nur fuer mich
 * bitte die A/B-Position lassen."*
 *
 * ⚠️ „Was guenstiger ist" ist im Code NICHT entscheidbar: fuer Lyria steht
 *    eine Zahl da (0,08 $ je Lied, server/lyria-musik.ts), fuer die Musik von
 *    ElevenLabs keine — die laeuft ueber Guthaben des Abos. Deshalb steht
 *    hier die bisherige Hauptfassung, und die Entscheidung haengt an EINEM
 *    Wort. Wer sie umdreht, aendert diese Zeile und sonst nichts.
 */
const HAUPT_ANBIETER: KlangStueck["anbieter"] = "elevenlabs";

interface KlangBlockUmgebung {
  /** Tagesschlüssel (YYYY-MM-DD) — bestimmt Anfrage und Dateiname. */
  tag: string;
  /** Schon bekannte Fassungen; fehlt das, holt der Block sie selbst. */
  stuecke?: KlangStueck[] | null;
  /** Rohantwort statt Liste — der Kasten liest die Fassungen selbst heraus. */
  antwort?: { musicUrl?: string | null; anbieter?: string } | null;
  lyrics?: string | null;
  /**
   * Beide Anbieter nebeneinander zeigen? (16.09.2026)
   *
   * Vorgabe NEIN — ein Kasten, ein Knopfpaar, keine Anbieterfrage. Das
   * Nebeneinander ist ein Vergleich fuer die Entwicklung, kein Angebot an
   * den Nutzer. Martin schaltet es fuer sich ein.
   *
   * ⚠️ Der sparsame Fall ist die VORGABE, nicht die Ausnahme: vergisst ein
   *    Aufrufer die Angabe, sieht ein Nutzer einen Kasten zu wenig — nie
   *    einen zu viel, der Geld kostet.
   */
  alleAnbieter?: boolean;
}

/**
 * Der Dateiname beim Sichern.
 *
 * ⚠️ Er trug bis zum 08.09. IMMER das heutige Datum — auch beim Stück vom
 *    3. September. Und mit zwei Fassungen desselben Tages hätten beide
 *    denselben Namen getragen und einander in „Dateien" überschrieben.
 */
export function klangDateiname(
  url: string,
  tag: string,
  anbieter: KlangStueck["anbieter"],
  nr = 1,
): string {
  const endung = url.split(".").pop()?.split("?")[0] || "mp3";
  const wer = anbieter === "lyria" ? "-Lyria" : "";
  // Ab der zweiten Fassung gehoert die Nummer in den Namen, sonst
  // ueberschreiben sich die Fassungen in „Dateien".
  const zahl = nr > 1 ? `-${nr}` : "";
  return `Klang-des-Tages-${tag}${wer}${zahl}.${endung}`;
}

/**
 * Die Fassungen aus einer Serverantwort lesen.
 *
 * ⚠️ `stuecke` gibt es erst seit dem 08.09. Antwortet ein aelterer Server
 *    (oder eine Huelle mit altem Zwischenspeicher) nur mit `musicUrl`, dann
 *    ist das EINE Fassung — und nicht etwa keine. Ohne diese Zeile stuende
 *    der Kasten leer, obwohl Musik da ist.
 */
export function ausAntwort(d: {
  stuecke?: KlangStueck[];
  musicUrl?: string | null;
  anbieter?: string;
}): KlangStueck[] {
  if (Array.isArray(d.stuecke)) return d.stuecke;
  if (!d.musicUrl) return [];
  return [
    {
      url: d.musicUrl,
      anbieter: d.anbieter === "lyria" ? "lyria" : "elevenlabs",
      nr: 1,
    },
  ];
}

/** Der Liedtext, aufklappbar — einmal für beide Fassungen. */
export function baueLiedtext(lyrics: string): HTMLElement {
  const lade = document.createElement("details");
  lade.className = "essence-music-lyrics-lade";
  const kopf = document.createElement("summary");
  kopf.textContent = "Liedtext";
  lade.appendChild(kopf);
  const p = document.createElement("p");
  p.className = "essence-music-lyrics";
  p.textContent = `„${lyrics.trim()}“`;
  lade.appendChild(p);
  return lade;
}

/**
 * Zeigt das Spiegel-Blatt den Reiter „Klang des Tages"? (23.09.2026, Ticket 2549)
 *
 * Bisher nur, wenn ein Klang da ist — ein leerer zweiter Reiter wäre eine
 * Wahl ohne Inhalt (18.09.). Seit die Morgenmeditation den Klang des
 * Nachtlaufs ersetzt, ist morgens aber meist KEINER da, und der Hauptnutzer
 * erreichte seine Komponieren-Knöpfe nur noch über den Verlauf (Prüfung
 * 23.09.). Für ihn ist der Reiter darum nie leer: Er trägt die Knöpfe.
 *
 * ⚠️ Alle anderen sehen ihn weiter nur mit Klang — ihnen baut der Kasten
 *    keine Knöpfe (16.09.), ein Reiter ohne Klang wäre für sie leer.
 */
export function klangReiterZeigen(klangDa: boolean): boolean {
  return klangDa || istHauptnutzer();
}

/**
 * Der ganze Kasten „Klang des Tages".
 *
 * Gibt sofort ein Element zurück und füllt sich nach, sobald die Fassungen
 * bekannt sind — der Spiegel soll nicht auf die Mediathek warten.
 */
export function baueKlangBlock(u: KlangBlockUmgebung): HTMLElement {
  const box = document.createElement("div");
  box.className = "essence-music";
  const head = document.createElement("h3");
  head.className = "essence-music-head";
  head.textContent = "Klang des Tages";
  box.appendChild(head);

  const textZone = document.createElement("div");
  textZone.className = "essence-music-text";

  /** Was gerade da ist — die eine Wahrheit, an der alle Kästen hängen. */
  let stuecke: KlangStueck[] =
    u.stuecke ?? (u.antwort ? ausAntwort(u.antwort) : []);
  let lyrics = u.lyrics ?? "";

  const fassungen = new Map<KlangStueck["anbieter"], FassungsKasten>();
  const gezeigte = u.alleAnbieter
    ? ANBIETER
    : ANBIETER.filter((a) => a.id === HAUPT_ANBIETER);
  for (const a of gezeigte) {
    const k = baueFassung(
      a.id,
      // Steht nur EIN Kasten da, ist der Name des Anbieters keine
      // Unterscheidung mehr, sondern eine Vokabel, die niemand braucht.
      gezeigte.length > 1 ? a.name : "",
      u,
      () => stuecke,
      aktualisiere,
      () => frischHolen(),
      // Steht der Text schon einmal unten, gehoert er nicht auch noch
      // unter jedes Stueck.
      () => gemeinsamerText(stuecke, lyrics) === null,
      () => lyrics,
    );
    fassungen.set(a.id, k);
    box.appendChild(k.wurzel);
  }
  box.appendChild(textZone);

  function aktualisiere(neue: KlangStueck[], neuerText?: string): void {
    stuecke = neue;
    if (typeof neuerText === "string" && neuerText.trim()) lyrics = neuerText;
    for (const a of gezeigte) fassungen.get(a.id)?.zeichne();
    textZone.replaceChildren();
    const gemeinsam = gemeinsamerText(stuecke, lyrics);
    if (gemeinsam) textZone.appendChild(baueLiedtext(gemeinsam));
  }

  aktualisiere(stuecke, lyrics);

  function frischHolen(): Promise<void> {
    return fetch(
      `/api/essence-mirror/music?date=${encodeURIComponent(u.tag)}`,
      { credentials: "same-origin" },
    )
      .then((r) => r.json())
      .then(
        (d: {
          stuecke?: KlangStueck[];
          musicUrl?: string | null;
          anbieter?: string;
          lyrics?: string;
        }) => {
          aktualisiere(ausAntwort(d), d.lyrics ?? "");
        },
      )
      .catch(() => {
        /* Kein Netz: der Kasten steht, die Knöpfe tun es auch. */
      });
  }
  // Nur nachsehen, wenn der Aufrufer nichts mitgegeben hat.
  if (!u.stuecke && !u.antwort) void frischHolen();

  return box;
}

interface FassungsKasten {
  wurzel: HTMLElement;
  zeichne: () => void;
}

/**
 * Eine Meldung, die auch dort ankommt, wo es kein `alert` gibt.
 *
 * ⚠️ Im Test (jsdom) existiert `window.alert` NICHT — der Fehlerzweig warf
 *    dann selbst und riss die Zusage mit. Ein Fehlerweg, der am Fehler
 *    scheitert, ist schlimmer als gar keiner.
 */
function sagBescheid(text: string): void {
  if (typeof window !== "undefined" && typeof window.alert === "function") {
    window.alert(text);
    return;
  }
  console.warn("[klang]", text);
}

/**
 * Ein Anbieter-Kasten: Name, Spieler, zwei Knöpfe.
 *
 * ⚠️ Die Knöpfe schicken IMMER `force: true`. Ein Druck heißt „mach mir
 *    diese Fassung" — ob schon eine da ist, entscheidet nur die Beschriftung
 *    („Neu: …"), nicht der Auftrag. Die alte Kopplung `force: mhas` war die
 *    Ursache der Fehlmeldung vom 08.09.
 */
function baueFassung(
  id: KlangStueck["anbieter"],
  name: string,
  u: KlangBlockUmgebung,
  hole: () => KlangStueck[],
  nachErfolg: (stuecke: KlangStueck[], lyrics?: string) => void,
  /** Beim Server nachfragen — nur der kennt die Nummer der neuen Fassung. */
  nachladen: () => Promise<void>,
  /** Tragen die Fassungen verschiedene Texte? Dann gehoert er ans Stueck. */
  textAmStueck: () => boolean,
  /** Der Tages-Text — Rueckfall fuer Stuecke ohne eigenen. */
  tagesText: () => string,
): FassungsKasten {
  const wurzel = document.createElement("div");
  wurzel.className = "essence-klang-fassung";
  wurzel.dataset.anbieter = id;

  const kopf = document.createElement("p");
  kopf.className = "essence-klang-name";
  kopf.textContent = name;
  // Ohne Namen keine leere Zeile — ein Absatz ohne Text nimmt trotzdem Platz.
  kopf.hidden = name === "";

  const zone = document.createElement("div");
  zone.className = "essence-klang-spieler";

  const reihe = document.createElement("div");
  reihe.className = "essence-music-btnrow";

  const mach = (vocals: boolean): HTMLButtonElement => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "btn btn--ghost btn--sm essence-music-btn";
    b.addEventListener("click", () => {
      const knoepfe = [...reihe.querySelectorAll("button")];
      knoepfe.forEach((x) => ((x).disabled = true));
      b.textContent = "Komponiere … (~1 Minute)";
      void fetch("/api/essence-mirror/music", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({
          date: u.tag,
          force: true,
          vocals,
          anbieter: id,
        }),
      })
        .then((r) => r.json())
        .then(
          (d: {
            ok?: boolean;
            musicUrl?: string;
            lyrics?: string;
            error?: string;
          }) => {
            if (d.ok && d.musicUrl) {
              /*
               * ⚠️ Die neue Fassung kommt DAZU, nichts wird ersetzt. Ihre
               *    Nummer kennt nur der Server — deshalb einmal nachfragen
               *    statt raten. Bis die Antwort da ist, steht sie schon als
               *    letzte Fassung im Kasten.
               */
              const vorhandene = hole();
              const meine = vorhandene.filter((s) => s.anbieter === id).length;
              nachErfolg(
                [
                  { url: d.musicUrl, anbieter: id, nr: meine + 1 },
                  ...vorhandene,
                ],
                d.lyrics,
              );
              void nachladen();
            } else {
              sagBescheid(d.error || "Fehlgeschlagen — nochmal versuchen?");
            }
          },
        )
        .catch(() => sagBescheid("Fehler — nochmal versuchen?"))
        .finally(() => {
          knoepfe.forEach((x) => ((x).disabled = false));
          beschrifte();
        });
    });
    return b;
  };

  const mitGesang = mach(true);
  const nurKlang = mach(false);
  reihe.append(mitGesang, nurKlang);
  /*
   * ⚠️ 16.09.2026 (Martin) — DIE ERZEUGEN-KNOEPFE SIND NICHT FUER KUNDEN.
   *
   * *„Bei Albert und Kunden kann man im Prinzip die ‚noch eine Musik
   * erzeugen' alles weggeben. … Damit ist wirklich nur der Player."*
   *
   * Jeder Druck erzeugt ein Stueck und kostet. Ein Angebot, das jemand nicht
   * einordnen kann, ist kein Angebot, sondern eine Falle — und die Musik
   * entsteht ohnehin von selbst zum Spiegel.
   *
   * ⚠️ NICHT nur per CSS versteckt: ein Knopf, den es im Blatt noch gibt,
   *    ist ueber die Tastatur und ueber Hilfsmittel weiterhin erreichbar.
   *    Was niemand druecken soll, wird gar nicht erst gebaut.
   */
  if (istHauptnutzer()) wurzel.append(kopf, zone, reihe);
  else wurzel.append(kopf, zone);

  function meine(): KlangStueck[] {
    return hole().filter((s) => s.anbieter === id);
  }

  function beschrifte(): void {
    /*
     * 08.09.2026 — „Noch eine" statt „Neu": Martin: *„Cool wäre, wenn ich
     * verschiedene Versionen generieren kann, ohne dass die alten verloren
     * gehen."* „Neu" hiesse ersetzen, und genau das tut der Knopf nicht mehr.
     */
    const da = meine().length > 0;
    mitGesang.textContent = da ? "Noch eine: mit Gesang" : "Mit Gesang";
    nurKlang.textContent = da ? "Noch eine: nur Klang" : "Nur Klang";
  }

  function zeichne(): void {
    beschrifte();
    const liste = meine();
    zone.replaceChildren();
    if (liste.length === 0) {
      wurzel.dataset.leer = "ja";
      return;
    }
    delete wurzel.dataset.leer;
    void import("./audio-player.js").then(({ buildAudioPlayer }) => {
      // Neueste zuerst — die letzte Fassung ist die, die er gerade wollte.
      for (const s of liste) {
        const nr = s.nr ?? 1;
        const halter = document.createElement("div");
        halter.className = "essence-klang-fassung-stueck";
        // Nur ab der zweiten Fassung eine Zeile — sonst waere „Fassung 1"
        // eine Auskunft ueber etwas, das es nur einmal gibt.
        if (liste.length > 1) {
          const zahl = document.createElement("p");
          zahl.className = "essence-klang-nr";
          zahl.textContent = `Fassung ${nr}`;
          halter.appendChild(zahl);
        }
        const spielerZone = document.createElement("div");
        halter.appendChild(spielerZone);
        // Ticket 2228: sichtbar machen statt wie ein Fehler wirken zu
        // lassen, wenn dieses Stueck nicht aus dem aktuellsten Lauf stammt.
        const hinweisText = komponiertHinweis(s.createdAt);
        if (hinweisText) {
          const hinweis = document.createElement("p");
          hinweis.className = "tool-music-hint";
          hinweis.textContent = hinweisText;
          halter.appendChild(hinweis);
        }
        zone.appendChild(halter);
        const bauen = (favoritAn: boolean | null): void => {
          spielerZone.replaceChildren(
            buildAudioPlayer(s.url, {
              titel:
                liste.length > 1
                  ? `Klang des Tages · ${name} ${nr}`
                  : `Klang des Tages · ${name}`,
              sichern: { dateiname: klangDateiname(s.url, u.tag, id, nr) },
              ...(favoritAn === null
                ? {}
                : {
                    merken: {
                      an: favoritAn,
                      umschalten: (neu: boolean) =>
                        setzeKlangFavorit(s.url, neu, u.tag, id),
                    },
                  }),
            }),
          );
        };
        /*
         * ⚠️ Der Herz-Stand kommt VOR dem Bauen. Ihn hinterher „anzuklicken"
         *    wäre ein zweiter Server-Aufruf und legte den Favoriten gleich
         *    wieder um. Scheitert die Abfrage, gibt es lieber kein Herz als
         *    ein falsches.
         */
        if (textAmStueck()) {
          const eigen = (s.lyrics ?? tagesText()).trim();
          if (eigen) halter.appendChild(baueLiedtext(eigen));
          else if (s.instrumental) {
            const hin = document.createElement("p");
            hin.className = "essence-klang-instrumental";
            hin.textContent = "Instrumental — ohne Gesang";
            halter.appendChild(hin);
          }
        }
        void klangFavoritStand(s.url)
          .then((an) => bauen(an))
          .catch(() => bauen(null));
      }
    });
  }

  return { wurzel, zeichne };
}

/**
 * Lieblingsstücke — das Herz am Spieler. (07.09.2026)
 *
 * Martin: *„ein Herz-Button als Favorit, damit ich die Lieder auch zusätzlich
 * speichern und abspielen kann. Eine eigene Mediathek für Musikstücke."*
 *
 * ⚠️ 08.09. aus app.ts hierher gezogen: die Mediathek braucht dieselben zwei
 *    Wege, und zwei Kopien waeren zwei Wahrheiten ueber dasselbe Herz.
 */
async function klangFavoritStand(url: string): Promise<boolean> {
  try {
    const r = await fetch("/api/musik/favoriten", { credentials: "same-origin" });
    if (!r.ok) return false;
    const d = (await r.json()) as { stuecke?: Array<{ url?: string }> };
    const datei = url.split("/").pop()?.split("?")[0];
    return (d.stuecke ?? []).some(
      (s) => s.url?.split("/").pop()?.split("?")[0] === datei,
    );
  } catch {
    return false;
  }
}

/** Gibt den TATSAECHLICHEN Stand zurueck — nicht den gewuenschten. */
export async function setzeKlangFavorit(
  url: string,
  an: boolean,
  tag?: string,
  /**
   * Wer das Stueck komponiert hat. (15.09.2026, Martins A-B-Test)
   *
   * ⚠️ Ohne diese Angabe sagt die Statistik nur, DASS ihm eines gefiel —
   *    nicht, wem. Genau das war bis heute der Fall.
   */
  anbieter?: "elevenlabs" | "lyria",
): Promise<boolean> {
  try {
    const r = await fetch("/api/musik/favorit", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        url,
        titel: "Klang des Tages",
        // 08.09.2026: der Tag DES STUECKS, nicht der heutige — sonst
        // liegt das Lieblingsstueck vom 3. September unter dem 8.
        // ⚠️ 17.09.2026 — der Rueckfall stand auf HEUTE und widersprach damit
        //    dem Satz darueber. Jetzt sagt er dasselbe: der Tag des Stuecks.
        tag: tag ?? spiegelStandardTag(),
        an,
        ...(anbieter ? { anbieter } : {}),
      }),
    });
    if (!r.ok) return !an;
    const d = (await r.json()) as { an?: boolean };
    return d.an === true;
  } catch {
    // Nicht gespeichert — dann bleibt das Herz, wie es war, statt zu luegen.
    return !an;
  }
}
